import { useState, useCallback, useRef } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { GripVertical, MoreHorizontal, RefreshCw, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Size configurations - how many grid cells each size occupies
const SIZE_SPANS = {
  small: { cols: 1, rows: 1 },   // 1x1
  medium: { cols: 2, rows: 1 },  // 2x1
  tall: { cols: 1, rows: 2 },    // 1x2
  large: { cols: 2, rows: 2 },   // 2x2
  xl: { cols: 3, rows: 2 },      // 3x2
};

// Available sizes for each tile type
const AVAILABLE_SIZES = {
  'compliance-overview': ['small', 'medium'],
  'expiring-soon': ['small', 'medium', 'tall'],
  'expired-requirements': ['small', 'medium'],
  'requirements-status': ['small', 'medium', 'large'],
  'my-tasks': ['small', 'medium', 'tall', 'large'],
  'tasks-due-soon': ['small', 'medium', 'tall'],
  'overdue-tasks': ['small', 'medium'],
  'task-progress': ['small', 'medium', 'large'],
  'team-overview': ['small', 'medium'],
  'team-directory': ['small', 'medium', 'large'],
  'notifications': ['small', 'medium', 'tall', 'large'],
  'unread-messages': ['small', 'medium'],
  'quick-checkin': ['small'],
  'create-task': ['small', 'medium'],
  'my-spaces': ['small', 'medium', 'large', 'xl'],
  'favorite-spaces': ['small', 'medium'],
};

// Size order for resizing (by total area, then by width)
const SIZE_ORDER = ['small', 'medium', 'tall', 'large', 'xl'];

export function TileWrapper({
  id,
  title,
  size = 'small',
  col = 0,
  row = 0,
  filter = {},
  spaces = [],
  children,
  onFilterChange,
  onSizeChange,
  loading = false,
  error = null,
  onRetry,
  isEditMode = false,
  onRemove,
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [previewSize, setPreviewSize] = useState(null);
  const previewSizeRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id, disabled: isResizing });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const tileType = id.split('-').slice(0, -1).join('-') || id;
  const availableSizes = AVAILABLE_SIZES[tileType] || ['small', 'medium', 'large'];
  // Don't change grid size during preview - only show indicator
  const sizeSpan = SIZE_SPANS[size] || SIZE_SPANS.small;

  const handleSpaceFilter = (spaceId) => {
    onFilterChange?.({ ...filter, spaceId: spaceId === 'all' ? null : spaceId });
  };

  // Find next/prev size based on drag direction
  const findTargetSize = useCallback((deltaX, deltaY) => {
    const currentSpan = SIZE_SPANS[size];
    const currentIndex = SIZE_ORDER.indexOf(size);

    // Calculate desired dimensions based on drag
    const threshold = 50; // pixels needed to trigger size change
    const colChange = Math.round(deltaX / threshold);
    const rowChange = Math.round(deltaY / threshold);

    const targetCols = Math.max(1, Math.min(3, currentSpan.cols + colChange));
    const targetRows = Math.max(1, Math.min(2, currentSpan.rows + rowChange));

    // Find best matching size from available sizes
    let bestSize = size;
    let bestScore = Infinity;

    for (const s of availableSizes) {
      const span = SIZE_SPANS[s];
      const score = Math.abs(span.cols - targetCols) + Math.abs(span.rows - targetRows);
      if (score < bestScore) {
        bestScore = score;
        bestSize = s;
      }
    }

    return bestSize;
  }, [size, availableSizes]);

  // Handle resize start
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    previewSizeRef.current = null;

    const startX = e.clientX;
    const startY = e.clientY;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      const newSize = findTargetSize(deltaX, deltaY);
      previewSizeRef.current = newSize;
      setPreviewSize(newSize);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);

      const finalSize = previewSizeRef.current;
      if (finalSize && finalSize !== size) {
        onSizeChange?.(finalSize);
      }
      setPreviewSize(null);
      previewSizeRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [findTargetSize, size, onSizeChange]);

  // CSS Grid positioning with aspect ratio for square-based cells
  const gridStyle = {
    ...style,
    gridColumn: `${col + 1} / span ${sizeSpan.cols}`,
    gridRow: `${row + 1} / span ${sizeSpan.rows}`,
    aspectRatio: `${sizeSpan.cols} / ${sizeSpan.rows}`,
    zIndex: isDragging ? 50 : isResizing ? 40 : 10,
  };

  return (
    <div
      ref={setNodeRef}
      style={gridStyle}
      className={cn(
        'relative',
        // Only transition opacity and transform, not grid properties
        'transition-[opacity,transform,box-shadow] duration-200',
        isDragging && 'opacity-50 scale-105',
        isResizing && 'ring-2 ring-primary'
      )}
    >
      <Card className={cn(
        'h-full flex flex-col overflow-hidden transition-shadow duration-200',
        isDragging && 'shadow-xl ring-2 ring-primary',
        isEditMode && 'ring-1 ring-dashed ring-gray-300 hover:ring-gray-400'
      )}>
        {/* Header - compact */}
        <CardHeader className="flex flex-row items-center justify-between py-0 px-2 h-8 border-b bg-gray-50/50 shrink-0">
          <div className="flex items-center gap-1.5">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600"
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
            <CardTitle className="text-xs font-medium">{title}</CardTitle>
            {filter.spaceId && (
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                Filtered
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Remove button - only in edit mode */}
            {isEditMode && onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                onClick={onRemove}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}

            {/* Actions Menu */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Space Filter */}
              {spaces.length > 0 && (
                <>
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium text-gray-500 mb-1">Filter by Space</p>
                    <Select
                      value={filter.spaceId || 'all'}
                      onValueChange={handleSpaceFilter}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="All Spaces" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Spaces</SelectItem>
                        {spaces.map((space) => (
                          <SelectItem key={space.id} value={space.id}>
                            {space.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}

              {/* Size Options */}
              <div className="px-2 py-1.5">
                <p className="text-xs font-medium text-gray-500 mb-1">Tile Size</p>
                <div className="flex flex-wrap gap-1">
                  {availableSizes.map((s) => (
                    <Button
                      key={s}
                      variant={size === s ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 text-xs px-2 capitalize"
                      onClick={() => onSizeChange?.(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>

              {onRetry && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onRetry}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </CardHeader>

        {/* Content - flex-1 fills remaining space */}
        <CardContent className="flex-1 p-3 overflow-hidden min-h-0">
          {loading ? (
            <TileLoading size={size} />
          ) : error ? (
            <TileError error={error} onRetry={onRetry} />
          ) : (
            children
          )}
        </CardContent>
      </Card>

      {/* Resize Handle - only in edit mode and if multiple sizes available */}
      {isEditMode && availableSizes.length > 1 && (
        <div
          onMouseDown={handleResizeStart}
          className={cn(
            "absolute bottom-0 right-0 w-6 h-6 cursor-se-resize z-20",
            "flex items-center justify-center",
            "bg-white border border-gray-300 rounded-tl-md rounded-br-lg",
            "hover:bg-primary hover:border-primary hover:text-white",
            "transition-colors shadow-sm",
            isResizing && "bg-primary border-primary text-white"
          )}
          title="Drag to resize"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            className="text-current"
          >
            <path
              d="M9 1L1 9M9 5L5 9M9 9L9 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </div>
      )}

      {/* Size preview indicator while resizing */}
      {isResizing && previewSize && previewSize !== size && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-lg z-30">
          {previewSize.charAt(0).toUpperCase() + previewSize.slice(1)}
          <span className="text-xs ml-1 opacity-75">
            ({SIZE_SPANS[previewSize].cols}×{SIZE_SPANS[previewSize].rows})
          </span>
        </div>
      )}
    </div>
  );
}

// Loading skeleton
function TileLoading({ size }) {
  const rows = size === 'small' ? 2 : size === 'tall' || size === 'large' ? 5 : 3;

  return (
    <div className="space-y-3">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Error state
function TileError({ error, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-4">
      <div className="bg-red-100 p-3 rounded-full mb-3">
        <AlertCircle className="h-6 w-6 text-red-600" />
      </div>
      <p className="text-sm text-gray-600 mb-3">
        {error?.message || 'Failed to load data'}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      )}
    </div>
  );
}

// Empty state
export function TileEmpty({ message = 'No data to show', icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-4 text-gray-400">
      {Icon && <Icon className="h-8 w-8 mb-2" />}
      <p className="text-sm">{message}</p>
    </div>
  );
}
